import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Hero } from "@/components/Hero";
import { HowItWorks } from "@/components/HowItWorks";
import { TripForm } from "@/components/TripForm";
import { LoadingState } from "@/components/LoadingState";
import { TripResult } from "@/components/result/TripResult";
import { startTrip, getTrip, waitForTrip } from "@/lib/api";
import { track } from "@/lib/analytics";
import { T, useLang } from "@/i18n";

export default function Home() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chars, setChars] = useState(0);
  const [error, setError] = useState("");
  const sectionRef = useRef(null);
  const location = useLocation();
  const { t, lang } = useLang();

  useEffect(() => {
    if (location.hash) setTimeout(() => document.querySelector(location.hash)?.scrollIntoView({ behavior: "smooth" }), 200);
    if (new URLSearchParams(location.search).get("payment") === "cancel") toast.info(t("Pagamento annullato. Puoi riprovare quando vuoi."));
  }, [location.hash, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const scroll = () => setTimeout(() => sectionRef.current?.scrollIntoView({ behavior: "smooth" }), 300);
    const scrollToResult = () => setTimeout(() => document.querySelector('[data-testid="ai-output-section"]')?.scrollIntoView({ behavior: "smooth" }), 400);
    setError("");
    (async () => {
      let tr;
      for (let i = 0; ; i += 1) {
        try { tr = await getTrip(id); break; } catch {
          if (cancelled) return;
          if (i >= 5) { setError(t("Itinerario non trovato.")); setLoading(false); return; }
          await new Promise((r) => setTimeout(r, 1500));
        }
      }
      if (cancelled) return;
      if (tr.status === "ready") { setTrip(tr); setLoading(false); scrollToResult(); return; }
      if (tr.status === "error") { setError(tr.error); setLoading(false); return; }
      setTrip(null); setLoading(true); setChars(tr.progress || 0); scroll();
      try {
        const done = await waitForTrip(id, setChars, () => cancelled);
        if (done && !cancelled) {
          setTrip(done); scrollToResult(); toast.success(t("Il tuo itinerario è pronto!"));
          track("itinerary_generated", { site_language: done.lang, destination_country: done.plan?.summary?.country || "", days: done.plan?.summary?.days || 0 });
        }
      } catch (e) {
        if (!cancelled) { setError(e.message); track("itinerary_generation_error", { error_type: "generation", site_language: lang }); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (payload) => {
    setLoading(true); setError(""); setTrip(null); setChars(0);
    try {
      const created = await startTrip(payload);
      navigate(`/viaggio/${created.id}`);
    } catch (e) {
      setError(e.message);
      setLoading(false);
      track("itinerary_generation_error", { error_type: "start", site_language: lang });
    }
  };

  return (
    <main>
      <SiteHeader />
      <Hero />
      <HowItWorks />
      <section id="crea-viaggio" ref={sectionRef} data-testid="create-trip-section" className="bg-slate-50 py-24 md:py-32 print:bg-white print:py-0">
        <div className="mx-auto max-w-6xl px-6 md:px-10">
          <div className="no-print mb-10 max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700"><T>Crea il tuo viaggio</T></p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-gray-900 lg:text-4xl"><T>Dicci cosa sogni. Al resto pensiamo noi.</T></h2>
          </div>
          <div className="no-print"><TripForm onSubmit={handleSubmit} loading={loading} /></div>
          {loading && <LoadingState chars={chars} />}
          {error && (
            <div data-testid="trip-error-alert" className="no-print mt-8 flex gap-3 rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-800">
              <AlertTriangle className="h-5 w-5 shrink-0" /><T>{error}</T>
            </div>
          )}
          {trip && <TripResult trip={trip} />}
        </div>
      </section>
      <SiteFooter />
    </main>
  );
}
